import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatJPY, formatDate } from "@/lib/format";
import {
  FileText, ClipboardList, Truck, ReceiptText, Wallet, Package,
  Link2, AlertTriangle, TrendingUp, Loader2, ArrowRight,
} from "lucide-react";
import { DOC_CONFIG } from "./config";

const DOC_LINKS = [
  { type: "quote", to: "/app/billing/quotes", icon: FileText },
  { type: "order", to: "/app/billing/orders", icon: ClipboardList },
  { type: "delivery", to: "/app/billing/delivery", icon: Truck },
  { type: "invoice", to: "/app/billing/invoices", icon: ReceiptText },
  { type: "receipt", to: "/app/billing/receipts", icon: ReceiptText },
  { type: "payment_notice", to: "/app/billing/payment-notices", icon: FileText },
  { type: "refund_notice", to: "/app/billing/refund-notices", icon: FileText },
];

function Stat({ icon: Icon, label, value, tone = "indigo", testid }) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    emerald: "bg-emerald-50 text-emerald-700",
  };
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5" data-testid={testid}>
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone]}`}><Icon className="h-4 w-4" /></div>
      <div className="mt-3 text-2xl font-bold text-stone-900">{value}</div>
      <div className="text-sm text-stone-500">{label}</div>
    </div>
  );
}

export default function BillingHub() {
  const [d, setD] = useState(null);
  const [names, setNames] = useState({});

  useEffect(() => {
    api.get("/billing/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/customers").then((r) => {
      const m = {}; (r.data || []).forEach((c) => (m[c.id] = c.name)); setNames(m);
    }).catch(() => {});
  }, []);

  if (!d) return <div className="py-20 text-center text-stone-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;

  return (
    <div data-testid="billing-hub">
      <div className="mb-6">
        <h1 className="font-display font-bold text-2xl text-stone-900">請求管理</h1>
        <p className="text-sm text-stone-500 mt-0.5">見積から入金・会計連携までを一元管理</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat icon={TrendingUp} label="請求総額" value={formatJPY(d.billed_total)} tone="indigo" testid="stat-billed" />
        <Stat icon={Wallet} label="未収金残高" value={formatJPY(d.outstanding)} tone="amber" testid="stat-outstanding" />
        <Stat icon={AlertTriangle} label="支払期限超過" value={formatJPY(d.overdue)} tone="red" testid="stat-overdue" />
        <Stat icon={Link2} label="承認待ち仕訳" value={`${d.pending_links} 件`} tone="emerald" testid="stat-pending-links" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h2 className="text-sm font-semibold text-stone-700 mb-3">帳票</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {DOC_LINKS.map(({ type, to, icon: Icon }) => (
              <Link key={type} to={to} className="group rounded-xl border border-stone-200 bg-white p-4 hover:border-indigo-300 hover:shadow-sm transition-all" data-testid={`hub-link-${type}`}>
                <div className="flex items-center justify-between">
                  <Icon className="h-5 w-5 text-indigo-600" />
                  <span className="text-lg font-bold text-stone-900">{d.counts[type] ?? 0}</span>
                </div>
                <div className="mt-2 text-sm font-medium text-stone-700 flex items-center gap-1">{DOC_CONFIG[type].title}<ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
              </Link>
            ))}
            <Link to="/app/billing/payments" className="group rounded-xl border border-stone-200 bg-white p-4 hover:border-indigo-300 hover:shadow-sm transition-all" data-testid="hub-link-payments">
              <Wallet className="h-5 w-5 text-indigo-600" />
              <div className="mt-2 text-sm font-medium text-stone-700">入金・消込</div>
            </Link>
            <Link to="/app/billing/products" className="group rounded-xl border border-stone-200 bg-white p-4 hover:border-indigo-300 hover:shadow-sm transition-all" data-testid="hub-link-products">
              <Package className="h-5 w-5 text-indigo-600" />
              <div className="mt-2 text-sm font-medium text-stone-700">商品・サービス</div>
            </Link>
            <Link to="/app/billing/accounting-link" className="group rounded-xl border border-stone-200 bg-white p-4 hover:border-indigo-300 hover:shadow-sm transition-all" data-testid="hub-link-accounting">
              <Link2 className="h-5 w-5 text-indigo-600" />
              <div className="mt-2 text-sm font-medium text-stone-700">会計連携（承認）</div>
            </Link>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-stone-700 mb-3">支払期限超過の請求</h2>
          <div className="rounded-xl border border-stone-200 bg-white divide-y divide-stone-100">
            {d.overdue_list.length === 0 ? (
              <div className="p-6 text-center text-sm text-stone-400">超過はありません</div>
            ) : d.overdue_list.map((o) => (
              <div key={o.id} className="p-3 flex items-center justify-between" data-testid={`overdue-${o.id}`}>
                <div>
                  <div className="text-sm font-medium text-stone-900">{o.doc_number}</div>
                  <div className="text-xs text-stone-500">{names[o.customer_id] || "—"} ・ 期限 {formatDate(o.due_date)}</div>
                </div>
                <div className="text-sm font-semibold text-red-600">{formatJPY(o.balance)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
